import {
  AutomationIcon,
  DashboardIcon,
  DesktopAppIcon,
  DocumentsIcon,
  HealthIcon,
  PartnerIcon,
  ReviewIcon,
  SettingsIcon,
  SystemAdminIcon,
  TaxonomyIcon,
  VisibilityIcon,
  type IconProps,
} from './icons';

import type { UserRole } from '../api/schema';

export interface NavItem {
  /** Label exactly as it appears in the Docsight design. */
  readonly label: string;
  /** Route the item links to. */
  readonly to: string;
  /** Prefix that keeps the item highlighted on nested routes. */
  readonly activePrefix: string;
  readonly Icon: (props: IconProps) => React.JSX.Element;
  /**
   * Roles that see this item. Every `ADMIN_NAV` item sets this; a
   * `PRIMARY_NAV` item omits it to show for every tenant role, or sets it to
   * narrow further (e.g. Settings, whose sub-screens are all admin-only).
   */
  readonly roles?: readonly UserRole[];
}

export interface SubNavItem {
  readonly label: string;
  readonly to: string;
}

/** Left-nav items above the ADMIN group. */
export const PRIMARY_NAV: readonly NavItem[] = [
  { label: 'Dashboard', to: '/dashboard', activePrefix: '/dashboard', Icon: DashboardIcon },
  { label: 'Documents', to: '/documents', activePrefix: '/documents', Icon: DocumentsIcon },
  { label: 'Review', to: '/review', activePrefix: '/review', Icon: ReviewIcon },
  { label: 'Automation', to: '/automation', activePrefix: '/automation', Icon: AutomationIcon },
  { label: 'Taxonomy', to: '/taxonomy', activePrefix: '/taxonomy', Icon: TaxonomyIcon },
  {
    label: 'Settings',
    to: '/settings/sources',
    activePrefix: '/settings',
    Icon: SettingsIcon,
    // Every Settings sub-screen requires a tenant_admin-only permission to
    // even load — analyst/viewer have nothing to see here (task #7 follow-up).
    roles: ['tenant_admin'],
  },
];

/** The primary-nav items visible to `role` (tenant roles only reach here). */
export function primaryNavForRole(role: UserRole | null | undefined): readonly NavItem[] {
  return PRIMARY_NAV.filter((item) => item.roles === undefined || (role != null && item.roles.includes(role)));
}

/** Route of the Desktop app sub-screen — see `settingsNav`. */
export const DESKTOP_APP_PATH = '/settings/desktop';

/** Expandable sub-nav under Settings. */
export const SETTINGS_NAV: readonly SubNavItem[] = [
  { label: 'Sources & API keys', to: '/settings/sources' },
  { label: 'Desktop app', to: DESKTOP_APP_PATH },
  { label: 'Mailboxes', to: '/settings/mailboxes' },
  { label: 'Locations & scanners', to: '/settings/locations' },
  { label: 'Retention', to: '/settings/retention' },
  { label: 'Users', to: '/settings/users' },
  { label: 'Branding', to: '/settings/branding' },
  { label: 'Webhooks', to: '/settings/webhooks' },
];

/**
 * The Settings sub-nav for this tenant.
 *
 * *Desktop app* is the one conditional entry: it appears only once a system
 * admin has published a build, which is exactly what the publish toggle on
 * Admin → Desktop app promises. A permanently empty row would make that toggle a
 * lie and hand every tenant a dead end. It carries the same label as the ADMIN
 * item deliberately — one feature, one name — and no one ever sees both, since
 * the roles that reach them are disjoint.
 */
export function settingsNav(hasDesktopApp: boolean): readonly SubNavItem[] {
  if (hasDesktopApp) return SETTINGS_NAV;
  return SETTINGS_NAV.filter((item) => item.to !== DESKTOP_APP_PATH);
}

/**
 * The ADMIN group, each item gated to the roles it belongs to:
 * - a **system_admin** (the platform owner) manages partners + the platform
 *   config inside *System admin*, and watches *Processing health* — it has no
 *   tenant, so it never sees the tenant sections or the partner's own console.
 * - a **partner_admin** gets its own *Partner console* (its tenants) + health.
 */
export const ADMIN_NAV: readonly NavItem[] = [
  {
    label: 'Partner console',
    to: '/admin/partner',
    activePrefix: '/admin/partner',
    Icon: PartnerIcon,
    roles: ['partner_admin'],
  },
  {
    label: 'Document fields',
    to: '/admin/document-fields',
    activePrefix: '/admin/document-fields',
    Icon: VisibilityIcon,
    roles: ['partner_admin'],
  },
  {
    label: 'System admin',
    to: '/admin/system',
    activePrefix: '/admin/system',
    Icon: SystemAdminIcon,
    roles: ['system_admin'],
  },
  {
    label: 'Desktop app',
    to: '/admin/desktop-app',
    activePrefix: '/admin/desktop-app',
    Icon: DesktopAppIcon,
    roles: ['system_admin'],
  },
  {
    label: 'Processing health',
    to: '/admin/health',
    activePrefix: '/admin/health',
    Icon: HealthIcon,
    roles: ['system_admin', 'partner_admin'],
  },
];

/** The tenant roles that see the tenant sections (Dashboard … Settings). */
const TENANT_NAV_ROLES: readonly UserRole[] = ['tenant_admin', 'tenant_analyst', 'tenant_viewer'];

/** True for a tenant role — platform roles (system/partner admin) have no tenant. */
export function showsTenantNav(role: UserRole | null | undefined): boolean {
  return role != null && TENANT_NAV_ROLES.includes(role);
}

/** The admin items visible to `role` (empty for a tenant role). */
export function adminNavForRole(role: UserRole | null | undefined): readonly NavItem[] {
  if (role == null) return [];
  return ADMIN_NAV.filter((item) => item.roles?.includes(role) ?? false);
}

/** Route prefix that owns the Settings sub-nav. */
export const SETTINGS_PREFIX = '/settings';

/** True when `pathname` is the item's route or nested beneath it. */
export function isNavItemActive(pathname: string, activePrefix: string): boolean {
  return pathname === activePrefix || pathname.startsWith(`${activePrefix}/`);
}
